import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardCheck, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/Button';
import { Skeleton, SkeletonRegion } from '@/components/Skeleton';
import { cn } from '@/lib/cn';
import { assinatura, useRascunho } from '@/lib/rascunho';
import { AvisoDeFalha, AvisoDeRegistroMudado } from '@/features/workflow/componentes';
import { classificarFalha, type Falha } from '@/features/workflow/recusa';
import { avaliarRisco, chavesDeMudanca, obterQuestionario } from '@/features/changes/changes.api';
import { EtiquetaDeRisco, Secao } from '@/features/changes/componentes';
import { ROTULO_DE_RISCO, type Mudanca } from '@/features/changes/tipos';
import { chaves } from '@/features/tickets/tickets.api';

/**
 * O questionário de risco — critério 4.2.
 *
 * ## Por que não existe um combo de nível de risco nesta tela
 *
 * "Risco alto seleciona a política do Comitê de Mudanças" só é auditável se a
 * pergunta *por que esta mudança é de risco alto?* tiver resposta seis meses
 * depois. Um combo registra a opinião de quem abriu; o questionário registra o
 * critério. Por isso o servidor **recusa** `riskLevel` no corpo — quem o define
 * é o gabarito, servido por `GET /changes/risk-questionnaire` e montado aqui
 * sem nenhuma cópia local.
 *
 * ## Pergunta em branco vale o pior caso
 *
 * É a decisão que faz o questionário ser controle e não formalismo: pontuar o
 * silêncio como "sem risco" transformaria não responder na forma mais rápida de
 * escapar do comitê. A tela diz isso **antes** de a pessoa enviar, e marca em
 * âmbar o que ficou sem resposta na última avaliação.
 */
export function AvaliacaoDeRisco({ mudanca }: { mudanca: Mudanca }) {
  const cliente = useQueryClient();
  const [falha, setFalha] = useState<Falha | null>(null);

  /**
   * As respostas, marcadas pelo que está gravado — ver `@/lib/rascunho`.
   *
   * Reavaliar por cima de respostas velhas gravaria um escore que ninguém
   * escolheu, e sem 409 nenhum para avisar: o servidor compara `version`, e a
   * versão que a tela tem é a nova. A marca é o próprio conjunto de respostas,
   * e não a versão, para que salvar os planos ou agendar — outros painéis da
   * mesma ficha — não trave este formulário sem ter mexido nele.
   */
  const rascunho = useRascunho<Record<string, string>>(
    () => mudanca.riskAnswers?.answers ?? {},
    assinatura(
      mudanca.riskAnswers?.questionnaireVersion,
      JSON.stringify(
        Object.entries(mudanca.riskAnswers?.answers ?? {}).sort(([a], [b]) => a.localeCompare(b)),
      ),
    ),
  );
  const respostas = rascunho.valor;

  const questionario = useQuery({
    queryKey: chavesDeMudanca.questionario,
    queryFn: obterQuestionario,
    staleTime: 60 * 60_000,
  });

  const avaliar = useMutation({
    mutationFn: () => avaliarRisco(mudanca.ticketId, respostas, mudanca.version),
    onMutate: () => setFalha(null),
    onSuccess: () => {
      rascunho.confirmar();
      // O nível de risco entra no `policySelector` do fluxo de mudança normal:
      // avaliar muda quais transições ficam disponíveis, então a raiz de
      // chamados recarrega junto.
      void cliente.invalidateQueries({ queryKey: chaves.raiz });
    },
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  if (questionario.isPending) {
    return (
      <SkeletonRegion label="Carregando o questionário de risco">
        <Skeleton className="h-40 w-full" />
      </SkeletonRegion>
    );
  }
  if (questionario.isError) {
    return (
      <AvisoDeFalha
        falha={classificarFalha(questionario.error)}
        aoRecarregar={() => void questionario.refetch()}
      />
    );
  }

  const gabarito = questionario.data;
  const semResposta = gabarito.questions.filter((pergunta) => !respostas[pergunta.key]);
  const versaoAvaliada = mudanca.riskAnswers?.questionnaireVersion ?? null;
  const gabaritoMudou = versaoAvaliada !== null && versaoAvaliada !== gabarito.version;

  return (
    <Secao
      titulo="Avaliação de risco"
      descricao={
        <>
          O nível não se escolhe: ele é derivado das respostas, e as respostas ficam guardadas
          junto com o escore. É o que faz “por que esta mudança foi para o comitê?” ter resposta
          depois. Gabarito v{gabarito.version}.
        </>
      }
      acao={
        <div className="flex items-center gap-2">
          <EtiquetaDeRisco nivel={mudanca.riskLevel} escore={mudanca.riskScore} />
        </div>
      }
    >
      {gabaritoMudou ? (
        <p className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning">
          Esta mudança foi avaliada com a versão {versaoAvaliada} do gabarito, e a versão corrente é
          a {gabarito.version}. O escore guardado continua sendo o que valeu na decisão — reavaliar
          o recalcula com os pesos de agora.
        </p>
      ) : null}

      <div className="space-y-4">
        {gabarito.questions.map((pergunta) => {
          const escolhida = respostas[pergunta.key] ?? '';
          return (
            <fieldset key={pergunta.key} className="space-y-1.5">
              <legend className="flex flex-wrap items-center gap-2 text-sm font-medium">
                {pergunta.label}
                {escolhida === '' ? (
                  <span className="inline-flex items-center gap-1 text-xs font-normal text-warning">
                    <TriangleAlert className="h-3 w-3" aria-hidden />
                    em branco vale o pior caso
                  </span>
                ) : null}
              </legend>
              <div className="grid gap-1 sm:grid-cols-2">
                {pergunta.options.map((opcao) => (
                  <label
                    key={opcao.value}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm transition-colors',
                      escolhida === opcao.value
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:bg-accent',
                    )}
                  >
                    <input
                      type="radio"
                      name={`risco-${pergunta.key}`}
                      value={opcao.value}
                      checked={escolhida === opcao.value}
                      onChange={() =>
                        rascunho.definir((atual) => ({ ...atual, [pergunta.key]: opcao.value }))
                      }
                    />
                    <span className="min-w-0 flex-1">{opcao.label}</span>
                    <span className="tabular-nums text-xs text-muted-foreground">
                      +{opcao.points}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          );
        })}
      </div>

      {avaliar.data ? (
        <div className="rounded-md border border-success/25 bg-success/5 px-3 py-2 text-sm text-success">
          Escore {avaliar.data.assessment.score} de 100 · risco{' '}
          <strong>{ROTULO_DE_RISCO[avaliar.data.assessment.level].toLowerCase()}</strong>
          {avaliar.data.assessment.unanswered.length > 0
            ? ` · ${avaliar.data.assessment.unanswered.length} pergunta(s) contadas pelo pior caso`
            : ''}
        </div>
      ) : null}

      {rascunho.desatualizado ? <AvisoDeRegistroMudado aoDescartar={rascunho.redefinir} /> : null}

      {falha ? (
        <AvisoDeFalha
          falha={falha}
          aoRecarregar={() => {
            rascunho.redefinir();
            void cliente.refetchQueries({ queryKey: chaves.item(mudanca.ticketId) });
          }}
        />
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          size="sm"
          loading={avaliar.isPending}
          disabled={rascunho.desatualizado}
          onClick={() => avaliar.mutate()}
          icon={<ClipboardCheck className="h-4 w-4" aria-hidden />}
        >
          Avaliar
        </Button>
        {semResposta.length > 0 ? (
          <span className="text-xs text-warning">
            {semResposta.length} pergunta(s) sem resposta — serão pontuadas pelo pior caso.
          </span>
        ) : null}
      </div>
    </Secao>
  );
}
